/**
 * ProgramAnalyticsPanel — cohort analytics for one learning program.
 *
 * Journey KPIs up top (enrollment, program completion, average progress),
 * then the day-by-day course table where each course shows its cohort
 * quiz score beside the course's all-time average — the delta reveals
 * whether the program context helps or hurts — and finally the
 * per-learner rollup (filterable by learner from the page's filter bar).
 */
import React, { useState } from 'react';
import { UserGroupIcon, CheckBadgeIcon, ChartBarIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import type { ProgramAnalytics } from '../../services/analytics';

const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v}%`;

const KPI_TILES = [
  { key: 'learners_enrolled', label: 'Learners Enrolled', Icon: UserGroupIcon, suffix: '' },
  { key: 'program_completion_rate', label: 'Program Completion', Icon: CheckBadgeIcon, suffix: '%' },
  { key: 'avg_progress', label: 'Average Progress', Icon: ChartBarIcon, suffix: '%' },
  { key: 'courses_total', label: 'Courses in Program', Icon: BookOpenIcon, suffix: '' },
] as const;

interface ProgramAnalyticsPanelProps {
  data: ProgramAnalytics;
  /** Optional learner filter (membership id) from the page's filter bar. */
  learnerFilter?: string;
}

const RECENT_LEARNERS = 5;

const ProgramAnalyticsPanel: React.FC<ProgramAnalyticsPanelProps> = ({
  data,
  learnerFilter,
}) => {
  const [showAllLearners, setShowAllLearners] = useState(false);
  const filtered = learnerFilter
    ? data.learners.filter((l) => l.user_id === learnerFilter)
    : data.learners;
  const learners = showAllLearners ? filtered : filtered.slice(0, RECENT_LEARNERS);

  if (data.kpis.learners_enrolled === 0) {
    return (
      <div className="card p-10 text-center space-y-2" data-testid="program-analytics-empty">
        <p className="text-base font-semibold text-text-primary">
          No learners enrolled in this program yet
        </p>
        <p className="text-sm text-text-secondary max-w-md mx-auto">
          Add an audience with learners in the program&rsquo;s Configure page —
          analytics fill in as they start learning. (Learners who took these
          courses outside the program are counted in the course&rsquo;s own
          analytics, not here.)
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="program-analytics-panel">
      {/* Journey KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI_TILES.map(({ key, label, Icon, suffix }) => (
          <div key={key} className="card p-5 flex items-center gap-3">
            <Icon className="w-8 h-8 text-primary-500 shrink-0" />
            <div>
              <p className="text-2xl font-bold text-text-primary leading-tight">
                {data.kpis[key]}
                {suffix}
              </p>
              <p className="text-xs text-text-secondary uppercase tracking-wide">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Day-by-day course performance */}
      <section className="card p-5 space-y-4">
        <div>
          <h3 className="text-base font-bold text-text-primary">Courses by Day</h3>
          <p className="text-xs text-text-secondary mt-1">
            All numbers below are for this program&rsquo;s learners only.
          </p>
        </div>
        {data.days.length === 0 ? (
          <p className="text-sm text-text-secondary">This program has no days yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-text-secondary uppercase tracking-wide border-b border-border-primary/20">
                  <th className="py-2 pr-4">Day</th>
                  <th className="py-2 pr-4">Course</th>
                  <th className="py-2 pr-4">Completed</th>
                  <th className="py-2 pr-4">Avg. Quiz Score</th>
                  <th className="py-2">Avg. Rating</th>
                </tr>
              </thead>
              <tbody>
                {data.days.flatMap((day) =>
                  day.courses.map((course, i) => {
                    const cohortScore = course.kpis?.quiz_avg ?? null;
                    const rating = course.kpis?.rating_avg ?? null;
                    const total = course.kpis?.learners ?? 0;
                    const done = Math.round(((course.kpis?.completion_rate ?? 0) * total) / 100);
                    return (
                      <tr
                        key={course.course_id}
                        className="border-b border-border-primary/10 last:border-0"
                      >
                        <td className="py-2.5 pr-4 font-semibold text-text-secondary">
                          {i === 0 ? `Day ${day.day_number}` : ''}
                        </td>
                        <td className="py-2.5 pr-4 font-medium text-text-primary">
                          {course.title}
                        </td>
                        <td className="py-2.5 pr-4 text-text-primary">
                          {total ? `${done} of ${total} learners` : '—'}
                        </td>
                        <td className="py-2.5 pr-4 text-text-primary">{fmtPct(cohortScore)}</td>
                        <td className="py-2.5 text-text-primary">
                          {rating === null ? '—' : `${rating}/5`}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Per-learner rollup */}
      <section className="card p-5 space-y-4">
        <h3 className="text-base font-bold text-text-primary">Learners</h3>
        {learners.length === 0 ? (
          <p className="text-sm text-text-secondary">
            No learner activity in this program yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-text-secondary uppercase tracking-wide border-b border-border-primary/20">
                  <th className="py-2 pr-4">Learner</th>
                  <th className="py-2 pr-4">Courses Completed</th>
                  <th className="py-2 pr-4">Program Progress</th>
                  <th className="py-2">Average Quiz Score</th>
                </tr>
              </thead>
              <tbody>
                {learners.map((l) => (
                  <tr
                    key={l.user_id}
                    className="border-b border-border-primary/10 last:border-0"
                  >
                    <td className="py-2.5 pr-4 font-medium text-text-primary">{l.name || '—'}</td>
                    <td className="py-2.5 pr-4 text-text-primary">
                      {l.courses_completed}/{l.courses_total}
                    </td>
                    <td className="py-2.5 pr-4 text-text-primary">{fmtPct(l.progress_percent)}</td>
                    <td className="py-2.5 text-text-primary">
                      {l.avg_score === null ? '—' : `${l.avg_score}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > RECENT_LEARNERS && !showAllLearners && (
              <div className="pt-3 text-center">
                <button
                  type="button"
                  onClick={() => setShowAllLearners(true)}
                  className="px-4 py-2 text-sm font-bold text-primary-500 border border-primary-500/40 rounded-full hover:bg-primary-500/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                  data-testid="program-learners-see-all"
                >
                  See All ({filtered.length})
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default ProgramAnalyticsPanel;
